import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useTranslation } from '../../../contexts/I18nProvider';
import * as localStorageHelper from '../../../utils/localStorageHelper';

interface DashboardBannerProps {
  /** Skiller lagret kollaps-valg per dashboard (operatør/lagersjef) */
  variant: 'operator' | 'manager';
  /** Tailwind gradient-klasser, f.eks. 'from-blue-500 to-purple-600' */
  gradientClassName: string;
  title: string;
  /** Status-/infolinje i banneret */
  info: React.ReactNode;
  /** Ekstra klasser (f.eks. margin) — brukes kun i utvidet tilstand */
  className?: string;
}

const STORAGE_KEY_PREFIX = 'bizclick_dashboard_banner_collapsed_';

/**
 * Felles velkomst-banner for Operatør- og Lagersjef-dashboardet.
 *
 * - Minimeres manuelt via chevron; valget huskes per dashboard i localStorage
 * - Minimert tilstand frigir hele området: kun et flytende chevron-ikon
 *   (0 høyde, absolutt posisjonert) for å hente banneret tilbake
 */
export const DashboardBanner: React.FC<DashboardBannerProps> = ({
  variant,
  gradientClassName,
  title,
  info,
  className = ''
}) => {
  const { t } = useTranslation();
  const storageKey = `${STORAGE_KEY_PREFIX}${variant}`;

  const [collapsed, setCollapsed] = useState<boolean>(
    () => localStorageHelper.getItem<boolean>(storageKey) ?? false
  );

  const toggle = () => {
    setCollapsed(prev => {
      localStorageHelper.setItem(storageKey, !prev);
      return !prev;
    });
  };

  if (collapsed) {
    // 0 høyde: knappen flyter over innholdet uten å oppta en egen linje
    return (
      <div className="relative h-0 z-10">
        <button
          onClick={toggle}
          aria-expanded={false}
          aria-label={t('common.dashboardBanner.expand', {}, 'Utvid banner')}
          title={t('common.dashboardBanner.expand', {}, 'Utvid banner')}
          className="absolute top-0 right-0 p-1 rounded-full bg-white border border-gray-200 text-gray-500 shadow-sm hover:bg-gray-50 hover:text-gray-700 transition-colors"
        >
          <ChevronDown size={16} />
        </button>
      </div>
    );
  }

  return (
    <div className={className}>
      <div className={`bg-gradient-to-r ${gradientClassName} text-white p-3 sm:p-4 rounded-lg shadow-lg`}>
        <div className="flex items-center gap-3 sm:gap-4">
          <img
            src="/compass-logo-white.png"
            alt="Compass"
            className="h-10 sm:h-12 object-contain flex-shrink-0"
          />
          <div className="w-px h-10 sm:h-12 bg-white/40 flex-shrink-0" />
          <div className="min-w-0 flex-1">
            <h2 className="text-base sm:text-xl font-bold mb-1 sm:mb-2 truncate">{title}</h2>
            <div className="text-xs sm:text-sm opacity-90">{info}</div>
          </div>
          <button
            onClick={toggle}
            aria-expanded={true}
            aria-label={t('common.dashboardBanner.collapse', {}, 'Minimer banner')}
            title={t('common.dashboardBanner.collapse', {}, 'Minimer banner')}
            className="flex-shrink-0 p-1.5 rounded-md hover:bg-white/15 active:bg-white/25 transition-colors self-start"
          >
            <ChevronUp size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};
